import { Usuario } from '@/app/models';

/**
 * Contratos de perfil de usuario para la web.
 *
 * Espejo de `sigd-new-app/src/app/core/types/user-profile.types.ts`. Si se cambia
 * uno, sincronizar el otro y los DTOs en `sigd-new-gateway/src/auth/dto`.
 */

/** Default defensivo cuando el backend no provee competencia. */
export const DEFAULT_COMPETENCIA = 'Operario' as const;

/** Tope máximo de la columna `usuario.competencia` en la DB. */
export const COMPETENCIA_MAX_LENGTH = 120;

/**
 * Payload para `PATCH /usuario/me/perfil`.
 * Refleja `UpdateMiPerfilDto` del gateway. Todos los campos son opcionales.
 *  - Strings: enviar valor → actualiza; omitir → deja igual.
 *  - `apellido`/`competencia`/`avatarUrl`: enviar `null` blanquea el valor.
 *  - `nombre`/`correo`: no admiten cadena vacía (son obligatorios).
 */
export interface UpdateMiPerfilDto {
    nombre?: string;
    apellido?: string | null;
    correo?: string;
    competencia?: string | null;
    avatarUrl?: string | null;
}

/** Vista sintética para componentes que muestran un usuario (header, listings). */
export interface UserAvatarLike {
    id?: number | null;
    nombre?: string | null;
    apellido?: string | null;
    nombreCompleto?: string | null;
    usuario?: string | null;
    correo?: string | null;
    avatar?: string | null;
    avatarUrl?: string | null;
    competencia?: string | null;
}

/**
 * Resuelve la URL absoluta del avatar para el `<img>`.
 *
 * Preferencia:
 *  1. `avatarUrl` (URL absoluta devuelta por storage para nuevos uploads).
 *  2. `avatar` legacy (path relativo; se normaliza contra `apiBase`).
 *
 * @param apiBase Valor de `environment.api` (con o sin slash final).
 */
export function resolveAvatarUrl(
    profile: UserAvatarLike | null | undefined,
    apiBase: string,
): string | null {
    if (!profile) return null;

    const direct = profile.avatarUrl?.trim();
    if (direct) {
        return /^https?:\/\//i.test(direct) ? direct : joinUrl(apiBase, direct);
    }

    const legacy = profile.avatar?.trim();
    if (legacy) {
        return /^https?:\/\//i.test(legacy) ? legacy : joinUrl(apiBase, legacy);
    }

    return null;
}

/** Devuelve la competencia con default defensivo si está vacía/nula. */
export function resolveCompetencia(profile: UserAvatarLike | null | undefined): string {
    const value = profile?.competencia?.trim();
    return value && value.length ? value : DEFAULT_COMPETENCIA;
}

/** Nombre para mostrar; cae a usuario o `Usuario` por defecto. */
export function resolveDisplayName(
    profile: UserAvatarLike | null | undefined,
    fallback = 'Usuario',
): string {
    if (!profile) return fallback;

    const nombreCompleto = profile.nombreCompleto?.trim();
    if (nombreCompleto) return nombreCompleto;

    const nombre = profile.nombre?.trim();
    const apellido = profile.apellido?.trim();
    if (nombre || apellido) return [nombre, apellido].filter(Boolean).join(' ');

    const usuario = profile.usuario?.trim();
    return usuario && usuario.length ? usuario : fallback;
}

/** Devuelve hasta 2 iniciales en mayúscula. */
export function resolveInitials(profile: UserAvatarLike | null | undefined): string {
    if (!profile) return '?';
    const nombre = profile.nombre?.trim() ?? '';
    const apellido = profile.apellido?.trim() ?? '';
    if (nombre || apellido) {
        const a = nombre[0] ?? '';
        const b = apellido[0] ?? '';
        const initials = `${a}${b}`.toUpperCase();
        return initials.length ? initials : '?';
    }

    const display = resolveDisplayName(profile, '').trim();
    if (display) {
        const parts = display.split(/\s+/).filter(Boolean);
        const first = parts[0]?.[0] ?? '';
        const second = parts[1]?.[0] ?? '';
        const initials = `${first}${second}`.toUpperCase();
        if (initials.length) return initials;
    }

    const usuario = profile.usuario?.trim();
    return usuario ? usuario[0]!.toUpperCase() : '?';
}

/**
 * Color de fondo determinístico (HSL) basado en el id/nombre, para que el
 * avatar de un mismo usuario sea siempre el mismo color.
 */
export function resolveAvatarBg(profile: UserAvatarLike | null | undefined): string {
    const seed = String(profile?.id ?? profile?.usuario ?? resolveDisplayName(profile, 'x'));
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
        hash = (hash << 5) - hash + seed.charCodeAt(i);
        hash |= 0;
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 55%, 88%)`;
}

/** Color de texto sobre el bg generado. */
export function resolveAvatarFg(profile: UserAvatarLike | null | undefined): string {
    const seed = String(profile?.id ?? profile?.usuario ?? resolveDisplayName(profile, 'x'));
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
        hash = (hash << 5) - hash + seed.charCodeAt(i);
        hash |= 0;
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 50%, 25%)`;
}

/** `Usuario` extendido con campos de Fase 1 (en caso que el modelo aún no los exponga). */
export type UsuarioConPerfilPersonal = Usuario & {
    competencia?: string | null;
    avatarUrl?: string | null;
};

function joinUrl(base: string, path: string): string {
    const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${cleanBase}${cleanPath}`;
}
